<template>
  <div class="o-messagecenter">
    <div class="o-container o-container--xlarge">
      <h2>{{ $t('messageCenter.title') }}</h2>

      <div class="messagecenter-notifyme">
        <bos-loader v-if="isLoading" inline />
        <switcher
          v-else
          :state="notifymeSwitcherState"
          variant="blue"
          textBefore
          :textOn="$t('messageCenter.notifyme.on')"
          :textOff="$t('messageCenter.notifyme.off')"
          @switcherChange="setNotifymeState"
        />
      </div>

      <div class="messagecenter-notifications">
        <b-table
          :busy="isLoading"
          :items="notifications"
          :fields="notificationFields"
          :sort-by.sync="notificationTable.sort.by"
          :sort-desk.sync="notificationTable.sort.desc"
          :tbody-tr-class="notificationTableRowClass"
          responsive
        >
          <template v-slot:table-busy>
            <bos-loader minHeight />
          </template>

          <template v-slot:cell(type)="data">
            <font-awesome-icon
              class="notification-icon"
              :icon="notificationTable.icons[data.value]"
              v-if="notificationTable.icons[data.value] !== undefined"
            />
          </template>

          <template v-slot:cell(date)="data">
            <time class="notification-date">{{
              $d(new Date(data.value), 'L')
            }}</time>
          </template>

          <template v-slot:cell(fair)="data">
            <div class="notification-fair">{{ data.value || '--' }}</div>
          </template>

          <template v-slot:cell(booth)="data">
            <div class="notification-booth">{{ data.value || '--' }}</div>
          </template>

          <template v-slot:cell(edit)="data">
            <router-link
              v-if="
                data.item.type === 'reminder' &&
                  data.item.fairId &&
                  data.item.boothId
              "
              class="notification-edit"
              :to="{
                name: 'FairDetail',
                params: { code: data.item.fairId, booth: data.item.boothId }
              }"
            >
              <font-awesome-icon icon="pen" />
            </router-link>
          </template>

          <template v-slot:cell(showDetails)="data">
            <bos-button
              class="notification-expand"
              blank
              @click.prevent="toggleDetails(data)"
            >
              <font-awesome-icon
                :icon="data.detailsShowing ? 'minus' : 'plus'"
              />
            </bos-button>
          </template>

          <template v-slot:row-details="row">
            <div class="notification-detail" v-html="row.item.body" />
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
import Switcher from '@/components/Switcher'
import BosLoader from '@/components/BosLoader'
import BosButton from '@/components/BosButton'
import { BTable } from 'bootstrap-vue'

export default {
  name: 'MessageCenter',
  data() {
    return {
      notifymeSwitcherState: false,
      notificationTable: {
        sort: {
          by: 'date',
          desc: false
        },
        fields: {
          post: ['edit', 'showDetails'],
          hidden: ['id', 'fairId', 'boothId', 'body', 'unread', '_showDetails']
        },
        icons: {
          reminder: 'exclamation-circle',
          information: 'info-circle'
        }
      }
    }
  },
  computed: {
    isLoading() {
      return this.$store.getters['notifications/isLoading']
    },
    notifymeState() {
      return this.$store.getters['notifications/getNotifymeState']
    },
    notifications() {
      return this.$store.getters['notifications/get']
    },
    notificationFields() {
      return []
        .concat(
          Object.keys(this.notifications[0] || {}),
          this.notificationTable.fields.post
        )
        .filter(key => !this.notificationTable.fields.hidden.includes(key))
        .map(key => {
          return {
            key: key,
            label: this.notificationTable.fields.post.includes(key)
              ? ''
              : this.$t(`messageCenter.notifications.table.head.${key}`),
            sortable: !this.notificationTable.fields.post.includes(key)
          }
        })
    }
  },
  methods: {
    getNotifications() {
      this.$store.dispatch('notifications/get')
    },
    setNotifymeState(state) {
      this.$store.dispatch('notifications/setNotifymeState', state)
    },
    notificationTableRowClass(item) {
      return `notification-type__${(item || {}).type}`
    },
    toggleDetails(row) {
      this.notifications.forEach((notification, notificationIndex) => {
        if (row.index !== notificationIndex) {
          this.$set(notification, '_showDetails', false)
        }
      })

      row.toggleDetails()
    }
  },
  created() {
    this.getNotifications()
  },
  watch: {
    notifymeState(newState) {
      this.notifymeSwitcherState = newState
    }
  },
  components: {
    Switcher,
    BosLoader,
    BosButton,
    BTable
  }
}
</script>

<style lang="scss">
@import '@/assets/css/variables.scss';

$messagecenter-margin: 4.5rem 0 !default;

$messagecenter-notifyme-margin: 2.25rem 0 0 !default;

$messagecenter-notificationtable-margin: 2.25rem 0 0 !default;

$messagecenter-notificationtable-icon-size: 1.25em !default;

$messagecenter-notificationtable-edit-size: 1em !default;
$messagecenter-notificationtable-edit-color: $brand-blue !default;

$messagecenter-notificationtable-expand-size: 0.75em !default;
$messagecenter-notificationtable-expand-color: $messagecenter-notificationtable-edit-color !default;

$messagecenter-notificationtable-rowdetail-font-size: $base-font-size !default;
$messagecenter-notificationtable-rowdetail-font-weight: $base-font-weight !default;
$messagecenter-notificationtable-rowdetail-color: $base-color !default;

$messagecenter-notificationtable-type-reminder-font-weight: inherit !default;
$messagecenter-notificationtable-type-reminder-color: inherit !default;
$messagecenter-notificationtable-type-reminder-icon-color: $brand-red !default;

$messagecenter-notificationtable-type-information-font-weight: $font-weight-bold !default;
$messagecenter-notificationtable-type-information-color: $brand-blue !default;
$messagecenter-notificationtable-type-information-icon-color: $brand-turquoise !default;

.o-messagecenter {
  margin: $messagecenter-margin;

  .messagecenter-notifyme {
    display: flex;
    justify-content: flex-end;
    margin: $messagecenter-notifyme-margin;
  }

  .messagecenter-notifications {
    margin: $messagecenter-notificationtable-margin;

    .b-table {
      tr {
        th {
          border-top: none;
          border-bottom: none;
        }

        th,
        td {
          text-align: left;
          vertical-align: top;
          border-top-color: #d7d7d7;

          p {
            margin: 1.5rem 0;
          }
        }

        .notification-icon {
          font-size: $messagecenter-notificationtable-icon-size;
        }

        .notification-edit {
          svg {
            font-size: $messagecenter-notificationtable-edit-size;
            color: $messagecenter-notificationtable-edit-color;
          }
        }

        .notification-expand {
          svg {
            font-size: $messagecenter-notificationtable-expand-size;
            color: $messagecenter-notificationtable-expand-color;
          }
        }

        .notification-detail {
          font-size: $messagecenter-notificationtable-rowdetail-font-size;
          font-weight: $messagecenter-notificationtable-rowdetail-font-weight;
          color: $messagecenter-notificationtable-rowdetail-color;
        }

        &.notification-type__reminder {
          font-weight: $messagecenter-notificationtable-type-reminder-font-weight;
          color: $messagecenter-notificationtable-type-reminder-color;

          .notification-icon {
            color: $messagecenter-notificationtable-type-reminder-icon-color;
          }
        }

        &.notification-type__information {
          font-weight: $messagecenter-notificationtable-type-information-font-weight;
          color: $messagecenter-notificationtable-type-information-color;

          .notification-icon {
            color: $messagecenter-notificationtable-type-information-icon-color;
          }

          .notification-edit {
            display: none;
          }
        }
      }

      tr:last-of-type td {
        border-bottom: 1px solid #d7d7d7;
      }
    }
  }
}
</style>
